import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API } from "../api";
import AddressManager from "../components/AddressManager";
import SecurityManager from "../components/SecurityManager";
import "../styles/style_profil.css";
import PageLoader from "../components/PageLoader";

export default function Profil() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    a2f_enabled: false,
  });

  const [emailData, setEmailData] = useState({
    oldMail: "",
    oldMailConfirm: "",
    newMail: "",
    newMailConfirm: "",
  });

  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const photoInputRef = useRef(null);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteCountdown, setDeleteCountdown] = useState(null);
  const deleteTimerRef = useRef(null);

  useEffect(() => {
    if (!loading && !user) navigate("/auth", { state: { defaultForm: "signin" } });
  }, [user, loading, navigate]);

  // Gérer le décompte de suppression
  useEffect(() => {
    if (deleteCountdown !== null && deleteCountdown > 0) {
      deleteTimerRef.current = setTimeout(() => {
        setDeleteCountdown(deleteCountdown - 1);
      }, 1000);
    } else if (deleteCountdown === 0) {
      setDeleteCountdown(null);
      setShowDeleteConfirm(false);
    }

    return () => {
      if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current);
    };
  }, [deleteCountdown]);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        phone: user.phone || "",
        a2f_enabled: user.login_metadata?.twoFA_enabled || false,
      });

      if (user.image_source === "local" && user.image_name && user.image_extension) setPhotoPreview(`${API.API_LINK}/file/pictures/avatars/source/${user.image_name}.${user.image_extension}`);
      else if (user.image_source === "web" && user.image_name) setPhotoPreview(user.image_name);
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setMessage({ type: "", text: "" });

    // géré lactivation du button ou non en fonction de si une data est deferente de la data de base \\

  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedPhoto(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setPhotoPreview(event.target?.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoClick = () => {
    photoInputRef.current?.click();
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: "", text: "" });

    try {
      let formDataToSend;

      // if (selectedPhoto) {
      // formDataToSend = new FormData();
      // formDataToSend.append("first_name", formData.first_name);
      // formDataToSend.append("last_name", formData.last_name);
      // formDataToSend.append("phone", formData.phone);
      // formDataToSend.append("avatar", selectedPhoto);
      // }

      // const response = await API.post("users/profile", formDataToSend, {
      //   headers: { "Content-Type": "multipart/form-data" },
      // });
      const response = await API.put("user/personalUpdate", { uuid: user?.uuid, email: user?.email, type: "data" }, formData );

      if (response.success) {
        setUser((prev) => ({
          ...prev,
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
          login_metadata: {
            twoFA_enabled: formData.a2f_enabled,
          },
        }));
        setSelectedPhoto(null);
        setMessage({ type: "success", text: "Profil mis à jour avec succès !" });
      } else {
        setMessage({ type: "error", text: response.message || "Erreur lors de la mise à jour" });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Erreur lors de la mise à jour du profil" });
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAccountClick = () => {
    setShowDeleteConfirm(true);
    setDeleteCountdown(30);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteCountdown(null);
    if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current);
  };

  const handleConfirmDelete = async () => {
    setIsSubmitting(true);
    try {
      // const response = await API.delete("user/deleteAccount", { uuid: user?.uuid, email: user?.email });

      // if (response.success) {
      //   setMessage({ type: "success", text: "Compte supprimé. Redirection..." });
      //   setTimeout(() => {
      //     navigate("/auth", { state: { defaultForm: "signin" } });
      //   }, 2000);
      // } else {
      //   setMessage({ type: "error", text: response.message || "Erreur lors de la suppression du compte" });
      //   handleCancelDelete();
      // }
    } catch (error) {
      setMessage({ type: "error", text: error.response?.data?.message || "Erreur lors de la suppression du compte" });
      console.error(error);
      handleCancelDelete();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <PageLoader label="Chargement du profil" />;
  if (!user) return null;

  return (
    <div className="profil-container">
      <div className="profil-wrapper">
        <div className="profil-header">
          <h1>Mon Profil</h1>
          <p>Gérez vos informations personnelles et la sécurité de votre compte</p>
        </div>

        {message.text && (
          <div className={`profil-message ${message.type}`}>
            <span>{message.text}</span>
          </div>
        )}

        <div className="profil-content">
          {/* Section Informations personnelles */}
          <div className="profil-section">
            <div className="profil-section-header">
              <h2>Informations Personnelles</h2>
              <span className="profil-section-icon">👤</span>
            </div>

            <form onSubmit={handleUpdateProfile} className="profil-form">
              {/* Photo de profil */}
              {/* <div className="profil-photo-section">
                <div className="profil-photo-container">
                  <img src={ photoPreview || `${API.API_LINK}/file/pictures/avatars/source/ico-connexion45x45.png` } alt="Avatar" className="profil-photo" />
                  <button type="button" className="profil-photo-button" onClick={handlePhotoClick} title="Changer la photo">📷</button>
                </div>
                <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoSelect} style={{ display: "none" }}/>
                <p className="profil-photo-info">
                  {selectedPhoto ? "✓ Photo sélectionnée" : "Cliquez pour changer votre photo"}
                </p>
              </div> */}

              {/* Champs texte */}
              <div className="profil-form-group">
                <label htmlFor="first_name">Prénom</label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  placeholder="Votre prénom"
                />
              </div>

              <div className="profil-form-group">
                <label htmlFor="last_name">Nom</label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  placeholder="Votre nom"
                />
              </div>

              {/* <div className="profil-form-group">
                <label>Rôle</label>
                <input
                  type="text"
                  value={user.role_slug || "Utilisateur"}
                  disabled
                  className="profil-input-disabled"
                />
              </div> */}

              <div className="profil-form-group">
                <label htmlFor="phone">Téléphone</label>
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+33 6 12 34 56 78" />
              </div>

              <button
                type="submit"
                className="profil-button profil-button-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Mise à jour..." : "Mettre à jour le profil"}
              </button>
            </form>
          </div>

          <AddressManager user={user} />

          <SecurityManager user={user} setUser={setUser} message={message} setMessage={setMessage} />

          {/* Section Suppression du compte */}
          <div className="profil-section">
            <div className="profil-section-header">
              <h2>Zone de Danger</h2>
              <span className="profil-section-icon">⚠️</span>
            </div>

            <div className="profil-danger-zone">
              <div className="profil-danger-content">
                <h3>Supprimer mon compte</h3>
                <p>Une fois votre compte supprimé, il n'y a aucun retour en arrière. Soyez certain.</p>

                {!showDeleteConfirm && (
                  <button
                    type="button"
                    className="profil-button profil-button-danger"
                    onClick={handleDeleteAccountClick}
                  >
                    Supprimer mon compte
                  </button>
                )}

                {showDeleteConfirm && (
                  <div className="profil-delete-confirmation">
                    <div className="profil-delete-warning">
                      <p>⚠️ Êtes-vous sûr ? Cette action est irréversible !</p>
                      {deleteCountdown !== null && (
                        <p className="profil-delete-timer">
                          Suppression dans : <span className="profil-countdown">{deleteCountdown}</span>s
                        </p>
                      )}
                    </div>

                    <div className="profil-delete-buttons">
                      <button
                        type="button"
                        className="profil-button profil-button-danger-confirm"
                        onClick={handleConfirmDelete}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Suppression..." : "Confirmer la suppression"}
                      </button>
                      <button
                        type="button"
                        className="profil-button profil-button-secondary"
                        onClick={handleCancelDelete}
                        disabled={isSubmitting}
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}